import { DateTime } from "luxon";

import type { CustomChatRoomItem } from "@/Modules/Chat/Models/Domain/Chat";
import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import type { CustomChatRoomItemMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import type { ChatStore } from "@/Modules/Chat/Store";
import type { UserStore } from "@/Modules/User/Store";
import type { ActivityCenterStore } from "@/Modules/ActivityCenter/Store";
import type { CalendarEventApi } from "@/Modules/Calendars/Models/Api";
import {
  ChatRoomItemTypeMessageWebSocket,
  CustomChatRoomItemTypeMessageWebSocket,
} from "@/Modules/Chat/Models/WebSocket";
import { ChatRoomItemType } from "@/Modules/Chat/Models/Domain/Chat";
import { ACNotificationTypeApi } from "@/Modules/Models/Api/ActivityCenter";

import useChatStore from "@/Modules/Chat/Store";
import useUserStore from "@/Modules/User/Store";
import useActivityCenterStore from "@/Modules/ActivityCenter/Store";
import useAppStore, { type AppStore } from "@/Modules/App/Store";
import useCalendarsStore, { type CalendarsStore } from "@/Modules/Calendars/Store";

import { APP_ROUTES } from "@/Generated/Routes/paths";

import { addNewEventWithNeutrals } from "@/Modules/Chat/MessageFilters/helpers/AddNeutralDateEvent";
import { hasValue, isObject } from "@/Utils/Objects";
import { unixSecondsToDate, dateToHoursAndMinutes, secondsToDuration, dateToDateUi } from "@/Utils/Date";

import { debounceUpdateNotificationBadge } from "@/Modules/ActivityCenter/utils";
import { isValidString } from "@/Utils/String";
import CalendarEventTransformer from "@/Modules/Transformers/Calendars/Event";
import { getDateTime as getDateTimeEvent } from "@/Components/Extend/XCalendar/utils";

export interface ExtraParamsCustomChatRoomItem {
  chatStore: ChatStore;
  userStore: UserStore;
  calendarsStore: CalendarsStore;
  activityCenterStore: ActivityCenterStore;
  appStore: AppStore;
}

export const isCustomChatRoomItem = (message: unknown): message is CustomChatRoomItemMessageWebSocket => {
  return isObject(message) && "etype" in message && message.etype === ChatRoomItemTypeMessageWebSocket.CUSTOM;
};

export const chatCustomChatRoomItemHandler = (
  message: CustomChatRoomItemMessageWebSocket,
  {
    chatStore = useChatStore(),
    userStore = useUserStore(),
    calendarsStore = useCalendarsStore(),
    activityCenterStore = useActivityCenterStore(),
    appStore = useAppStore(),
  }: ExtraParamsCustomChatRoomItem
): Promise<undefined> => {
  if (!hasValue(chatStore.rooms[message.chat])) {
    return Promise.resolve(undefined);
  }

  if (
    [
      CustomChatRoomItemTypeMessageWebSocket.NEW_ACTIVITY_CENTER_EVENT,
      CustomChatRoomItemTypeMessageWebSocket.UPDATED_ACTIVITY_CENTER_EVENT,
    ].includes(message.action) &&
    message.isNew === true
  ) {
    if (userStore.getUserIsTherapist && hasValue(chatStore.rooms[message.chat])) {
      debounceUpdateNotificationBadge(
        { userId: chatStore.rooms[message.chat]!.user.id, chatId: message.chat },
        activityCenterStore
      );
    }

    debounceUpdateNotificationBadge({ userId: undefined, chatId: undefined }, activityCenterStore);

    const [title, description, eventType] = (message.extra as string).split(";;").map((data) => data);

    if (eventType === ACNotificationTypeApi.NEW_COMMUNICATION_WITH_PUSH_CUSTOMER) {
      appStore.notificate({
        title: title!,
        body: description,
        redirect: { name: APP_ROUTES.activity.root.name },
      });
    }

    // this is because we need to prevent multiple calls. DON'T DELETE.
    return Promise.resolve(undefined);
  }

  const customChatRoomItem: CustomChatRoomItem =
    transformCustomChatRoomItemMessageWebSocketToCustomChatRoomItem(message, {
      userId: userStore.user?.id,
      username: chatStore.rooms[message.chat]!.user.username,
      userIsPatient: userStore.getUserIsPatient,
      timezone: userStore.user?.timeZone,
    });

  if (message.isNew === true) {
    if (
      [
        CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_REQUEST,
        CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_CANCELLED,
        CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_CHANGED,
        CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_EXPIRED,
        CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_RENEWED,
      ].includes(message.action)
    ) {
      if (userStore.getUserIsTherapist) {
        chatStore.loadChatRoomUser(message.chat);
      }
    } else if (message.action === CustomChatRoomItemTypeMessageWebSocket.BREAK_START) {
      const chatRoom = chatStore.rooms[message.chat];

      if (hasValue(chatRoom) && !hasValue(chatRoom.currentBreak)) {
        chatStore.reloadCurrentBreak(message.chat);
      }
    } else if (
      [
        CustomChatRoomItemTypeMessageWebSocket.CALENDAR_UPDATED,
        CustomChatRoomItemTypeMessageWebSocket.CALENDAR_EVENT_CREATED,
        CustomChatRoomItemTypeMessageWebSocket.CALENDAR_EVENT_DELETED,
        CustomChatRoomItemTypeMessageWebSocket.CALENDAR_EVENT_UPDATED,
      ].includes(message.action)
    ) {
      // just need to update the schedule of the patients because just the therapist has the possibility to
      // modify the appointments.
      const eventApi =
        hasValue(message.extra) &&
        !["string", "number"].includes(typeof message.extra) &&
        isValidString((message.extra as CalendarEventApi).patient_uuid)
          ? (message.extra as CalendarEventApi)
          : undefined;

      if (hasValue(eventApi)) {
        if (message.action === CustomChatRoomItemTypeMessageWebSocket.CALENDAR_EVENT_CREATED) {
          calendarsStore.events.push(CalendarEventTransformer.toDomain(eventApi));
        } else if (message.action === CustomChatRoomItemTypeMessageWebSocket.CALENDAR_EVENT_DELETED) {
          calendarsStore.events = calendarsStore.events.filter((event) => event.uuid !== eventApi.uuid);
        } else if (message.action === CustomChatRoomItemTypeMessageWebSocket.CALENDAR_EVENT_UPDATED) {
          calendarsStore.events = calendarsStore.events.map((event) =>
            event.uuid === eventApi.uuid ? CalendarEventTransformer.toDomain(eventApi) : event
          );
        }
      } else {
        calendarsStore.fetchCalendarEvents(calendarsStore.query);
      }
    }
  }

  if (hasValue(chatStore.rooms[message.chat]!.events)) {
    const events = addNewEventWithNeutrals(chatStore.rooms[message.chat]!.events, customChatRoomItem);

    chatStore.rooms[message.chat]!.events = events;
  } else {
    const events = addNewEventWithNeutrals([], customChatRoomItem);

    chatStore.rooms[message.chat]!.events = events;
  }

  return Promise.resolve(undefined);
};

const parseBreakDates = (stringDates: string) => {
  const [breakStart, breakEnd] = stringDates.split(";;").map((date) => dateToDateUi(date, "dd/MM/yy"));

  return { breakStart, breakEnd };
};

const parseScheduleDates = (dates: string | CalendarEventApi, timezone?: string) => {
  if (typeof dates === "string") {
    const splitDates = dates.split(";;");

    return {
      selectedDate: dateToDateUi(unixSecondsToDate(splitDates[0]!), "dd/MM/yy"),
      startHour: dateToDateUi(unixSecondsToDate(splitDates[0]!), "HH:mm"),
      endHour: dateToDateUi(unixSecondsToDate(splitDates[1]!), "HH:mm"),
    };
  } else {
    const ensuredTimezone = timezone ?? DateTime.now().zoneName;
    const startDate = getDateTimeEvent(dates.start_at).setZone(ensuredTimezone);
    const endDate = getDateTimeEvent(dates.end_at).setZone(ensuredTimezone);

    return {
      selectedDate: startDate.toFormat("dd/MM/yy"),
      startHour: startDate.toFormat("HH:mm"),
      endHour: endDate.toFormat("HH:mm"),
    };
  }
};

interface ParamsCustomChatRoomItem {
  userId?: string;
  username?: string;
  userIsPatient?: boolean;
  timezone?: string;
}

export const transformCustomChatRoomItemMessageWebSocketToCustomChatRoomItem = (
  customChatRoomItemMessageWebSocket: CustomChatRoomItemMessageWebSocket,
  baseOptions: ParamsCustomChatRoomItem = {}
): CustomChatRoomItem => {
  let type: ChatRoomItemType;
  let i18nKey: string | undefined = undefined;
  let params: Record<string, unknown> = {};
  const options = {
    userId: "",
    username: "",
    userIsPatient: false,
    timezone: "",
    ...baseOptions,
  };
  const isMine = options.userId === customChatRoomItemMessageWebSocket.user;

  switch (customChatRoomItemMessageWebSocket.action) {
    case CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_REQUEST:
      type = ChatRoomItemType.SUBSCRIPTION_REQUEST;
      i18nKey = "chat.message.plan-title";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.SHOW_PLANS:
      type = ChatRoomItemType.SHOW_PLANS;
      i18nKey = "chat.message.plan-title";
      params = {
        planUuid: customChatRoomItemMessageWebSocket.extra,
        isMine,
      };

      break;

    case CustomChatRoomItemTypeMessageWebSocket.PATIENT_COME_BACK:
      type = ChatRoomItemType.PATIENT_COME_BACK;
      i18nKey = "chat.message.patient_come_back";
      params = {
        isMine: false,
      };

      break;

    case CustomChatRoomItemTypeMessageWebSocket.PATIENT_SSO:
      type = ChatRoomItemType.PATIENT_SSO;
      i18nKey = "chat.message.patient_sso";
      params = {
        isMine: false,
      };

      break;

    case CustomChatRoomItemTypeMessageWebSocket.PATIENT_REASSIGN:
      type = ChatRoomItemType.PATIENT_REASSIGN;
      i18nKey = "chat.message.patient_reassign";
      params = {
        isMine: false,
      };

      break;

    case CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_CANCELLED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      i18nKey = isMine ? "chat.message.subscription_cancel" : "chat.message.user_subscription_cancel";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_RENEWED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      i18nKey = isMine ? "chat.message.subscription_renew" : "chat.message.user_subscription_renew";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_EXPIRED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      i18nKey = isMine ? "chat.message.subscription_expired" : "chat.message.user_subscription_expired";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.SUBSCRIPTION_CHANGED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = {
        username: options.username,
        planName: customChatRoomItemMessageWebSocket.extra,
      };
      i18nKey = isMine
        ? "chat.message.subscription_plan_change"
        : "chat.message.user_subscription_plan_change";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.PAYMENT_ERROR:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      i18nKey = "chat.message.renewal_pending_payment";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.PAYMENT_OK:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = {
        username: options.username,
        planName: customChatRoomItemMessageWebSocket.extra,
      };
      i18nKey = isMine
        ? "chat.message.you_have_subscribed_to_plan"
        : "chat.message.user_has_subscribed_to_plan";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.REASSIGN_REQUEST_COMPLETED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = {
        sessions: customChatRoomItemMessageWebSocket.extra,
      };
      i18nKey = "chat.message.reassign_request_completed";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.QUESTIONNAIRE_ANSWERED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = {
        username: options.username,
        questionnaire: customChatRoomItemMessageWebSocket.extra,
      };
      i18nKey = isMine
        ? "chat.message.questionnaire_complete"
        : "chat.message.user_has_completed_questionnaire";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.VIDEO_CALL_ENDED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = {
        endDate: secondsToDuration(customChatRoomItemMessageWebSocket.extra as number),
      };
      i18nKey = "chat.message.video_call.ended";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.VIDEO_CALL_MISSED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = {
        date: dateToHoursAndMinutes(unixSecondsToDate(customChatRoomItemMessageWebSocket.extra as number)),
      };
      i18nKey = "chat.message.video_call.missed";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.VIDEO_CALL_REJECTED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = {
        date: dateToHoursAndMinutes(unixSecondsToDate(customChatRoomItemMessageWebSocket.extra as number)),
      };
      i18nKey = "chat.message.video_call.refused";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.CONTRACT_SIGNED:
      type = ChatRoomItemType.NEUTRAL_TEXT;

      params = {
        contract: customChatRoomItemMessageWebSocket.extra,
      };
      i18nKey = "chat.message.contract_signed";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.NEW_CONTRACT:
      type = ChatRoomItemType.NEUTRAL_TEXT;

      params = {
        contract: customChatRoomItemMessageWebSocket.extra,
      };
      i18nKey = "chat.message.new_contract";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.BREAK_CREATED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = parseBreakDates(customChatRoomItemMessageWebSocket.extra as string);
      i18nKey = "chat.message.break.created";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.BREAK_DELETED:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      params = parseBreakDates(customChatRoomItemMessageWebSocket.extra as string);
      i18nKey = "chat.message.break.deleted";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.BREAK_START:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      i18nKey = "chat.message.break.start";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.BREAK_END:
      type = ChatRoomItemType.NEUTRAL_TEXT;
      i18nKey = "chat.message.break.end";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.CALENDAR_EVENT_CREATED:
      if (
        hasValue(customChatRoomItemMessageWebSocket.extra) &&
        !["string", "number"].includes(typeof customChatRoomItemMessageWebSocket.extra)
      ) {
        type = ChatRoomItemType.NEUTRAL_TEXT;
        params = parseScheduleDates(
          customChatRoomItemMessageWebSocket.extra as CalendarEventApi,
          options.timezone
        );
        i18nKey = "chat.message.calendars.event.created";
      } else {
        type = ChatRoomItemType.UNKNOWN;
      }

      break;
    case CustomChatRoomItemTypeMessageWebSocket.CALENDAR_EVENT_DELETED:
      if (
        hasValue(customChatRoomItemMessageWebSocket.extra) &&
        !["string", "number"].includes(typeof customChatRoomItemMessageWebSocket.extra) &&
        isValidString((customChatRoomItemMessageWebSocket.extra as CalendarEventApi).patient_uuid)
      ) {
        type = ChatRoomItemType.NEUTRAL_TEXT;
        params = parseScheduleDates(
          customChatRoomItemMessageWebSocket.extra as CalendarEventApi,
          options.timezone
        );
        i18nKey = "chat.message.calendars.event.cancelled";
      } else {
        type = ChatRoomItemType.UNKNOWN;
      }

      break;
    case CustomChatRoomItemTypeMessageWebSocket.UPDATED_ACTIVITY_CENTER_EVENT:
      type = ChatRoomItemType.UNKNOWN;
      i18nKey = "UPDATE ACTIVITY CENTER";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.NEW_ACTIVITY_CENTER_EVENT:
      type = ChatRoomItemType.UNKNOWN;
      i18nKey = "NEW ACTIVITY CENTER";

      break;
    case CustomChatRoomItemTypeMessageWebSocket.CLINIC_DISCHARGE:
      if (options.userIsPatient) {
        type = ChatRoomItemType.NEUTRAL_TEXT;
        params = parseScheduleDates(customChatRoomItemMessageWebSocket.extra as string, options.timezone);
        i18nKey = "chat.message.schedule.clinic_discharge_patient";
      } else {
        type = ChatRoomItemType.NEUTRAL_TEXT;
        params = {
          ...parseScheduleDates(customChatRoomItemMessageWebSocket.extra as string, options.timezone),
          username: options.username,
        };
        i18nKey = "chat.message.schedule.clinic_discharge_therapist";
      }

      break;
    case CustomChatRoomItemTypeMessageWebSocket.CHANGE_DURATION_REJECTED:
      type = ChatRoomItemType.NEUTRAL_TEXT_GREY;
      i18nKey = "chat.message.change_duration.rejected";
      params = {
        username: options.username,
      };

      break;
    default:
      type = ChatRoomItemType.UNKNOWN;
      i18nKey = "not implemented message: " + customChatRoomItemMessageWebSocket.action;
  }

  const createdAt = unixSecondsToDate(customChatRoomItemMessageWebSocket.created);

  const customChatRoomItem: CustomChatRoomItem = {
    i18nKey,
    id: customChatRoomItemMessageWebSocket.eid,
    createdAt,
    params,
    type: type!,
    old: !!customChatRoomItemMessageWebSocket.old,
    isReceived: !!customChatRoomItemMessageWebSocket.is_received,
  };

  return customChatRoomItem;
};

export default {
  isType: isCustomChatRoomItem,
  handler: chatCustomChatRoomItemHandler,
} as EventFilter<ExtraParamsCustomChatRoomItem>;
